import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { CombinedReportsRouteDefinition } from '../../combined-reports/combined-reports-route.definition';

import { DashboardRouteDefinition } from '../../dashboard/dashboard-route.definition';
import { DeviceManagementRouteDefinition } from '../../device-management/device-management-route.definition';
import { FloorRouteDefinition } from '../../floors/floor-route.definition';
import { PaymentsRouteDefinition } from '../../payments/payments-route.definition';
import { FinancialServicesRouteDefinition } from '../../financial-services/financial-services.definition';
import { PaymentsService } from '../../payments/payments.service';
import { PrintingRouteDefinition } from '../../printing/printing-route.definition';
import { ProductsRouteDefinition } from '../../products/products-route.definition';
import { ReportsRouteDefinition } from '../../reports/reports-route.definition';
import { CompanySettingsServiceHelper } from '../../settings/services/company-settings-helper.service';
import { SettingsRouteDefinition } from '../../settings/settings-route.definition';
import { SupportRouteDefinition } from '../../support/support-route.definition';
import { UserManagementRouteDefinition } from '../../user-management/user-management-route.definition';
import { WaiterRouteDefinition } from '../../waiter/waiter-route.definition';
import { PermissionService } from '../permission/permission.service';

import { AccessControlService } from './access-control.service';

import { RouteDefinition } from './route-definition';
import { TasksRouteDefinition } from 'app/tasks/tasks-route.definition';
import { environment } from 'environments/environment';
import { LsAccountingRouteDefinition } from 'app/ls-accounting/ls-accounting-route.definition';

@Injectable()
export class NavigationService {
  private isAuthenticated$: Observable<
    boolean
  > = this.accessControl.isLoggedIn();
  private hasEmployeePermission$: Observable<
    boolean
  > = this.accessControl.hasEmployeePermission();
  private isLoggedInAsPartner$: Observable<
    boolean
  > = this.accessControl.isLoggedInAsPartner();
  private hasManagerAccess$: Observable<
    boolean
  > = this.accessControl.hasManagerAccess();
  private canSeeReports$: Observable<
    boolean
  > = this.accessControl.hasReportAccess();
  private isValidAppVersion$: Observable<
    boolean
  > = this.accessControl.isValidAppVersion();
  private showClosingReports$: Observable<
    boolean
  > = this.accessControl.hasAccessToClosingReports();
  private showIrsReports$: Observable<
    boolean
  > = this.accessControl.showIrsReports();
  private hasMultipleLocations$: Observable<
    boolean
  > = this.accessControl.hasMultipleLocations();
  private numberOfLocations$: Observable<
    number
  > = this.accessControl.getNumberOfLocations();

  private hasProductsAccess$: Observable<
    boolean
  > = this.accessControl.hasProductsAccess();
  private hasKitchenModuleEnabled$: Observable<
    boolean
  > = this.accessControl.hasKitchenModuleEnabled();
  private isAllowedToConfigureCFD$: Observable<
    boolean
  > = this.accessControl.isAllowedToConfigureCFD();

  private isBlackboxAccount$: Observable<
    boolean
  > = this.accessControl.isBlackboxAccount();
  private isBlackboxV2Account$: Observable<
    boolean
  > = this.accessControl.isBlackboxV2Account();

  private isLsAccountingEnabled$: Observable<
    boolean
  > = this.accessControl.hasLsAccountingModuleEnabled();

  constructor(
    private accessControl: AccessControlService,
    private permission: PermissionService,
    private companySettingsServiceHelper: CompanySettingsServiceHelper,
    private paymentsServices: PaymentsService
  ) {}

  public getNavigationMenu(): Observable<Array<RouteDefinition>> {
    if (this.permission.isEmployee()) {
      return this.getEmployeeNavigationMenu();
    } else {
      return this.getNormalNavigationMenu();
    }
  }

  private getEmployeeNavigationMenu(): Observable<Array<RouteDefinition>> {
    return combineLatest(this.getWaiterRoutes()).pipe(
      map((waiterRoutes: Array<RouteDefinition>) => {
        return waiterRoutes || [];
      })
    );
  }

  private getNormalNavigationMenu(): Observable<Array<RouteDefinition>> {
    const mainRoutes = [
      {
        routes$: this.getReportsRoutes(),
        associatedRightName: [
          'Allow reporting access',
          'Manager - Allow reporting access'
        ]
      },
      {
        routes$: this.getCombinedReportsRoutes(),
        associatedRightName: [
          'Allow reporting access',
          'Manager - Allow reporting access'
        ]
      },
      {
        routes$: this.getUserManagementRoutes(),
        associatedRightName: 'Manager - Allow users access'
      },
      {
        routes$: this.getProductsRoutes(),
        associatedRightName: [
          'Allow product access',
          'Manager - Allow product access'
        ]
      },
      {
        routes$: this.getFloorsRoutes(),
        associatedRightName: 'Manager - Allow floors access'
      },
      {
        routes$: this.getDeviceManagementRoutes(),
        associatedRightName: 'Manager - Allow devices access'
      },
      {
        routes$: this.getPrintingRoutes(),
        associatedRightName: 'Manager - Allow printing access'
      },
      {
        routes$: this.getSupportRoutes(),
        associatedRightName: 'Manager - Allow support access'
      },
      {
        routes$: this.getPaymentsRoutes(),
        associatedRightName: 'Manager - Allow payments access'
      },
      {
        routes$: this.getSettingsRoutes(),
        associatedRightName: 'Manager - Allow settings access'
      },
      {
        routes$: this.getTasksRoutes(),
        associatedRightName: null
      },
      {
        routes$: this.getLsAccountingRoutes(),
        associatedRightName: null
      },
      {
        routes$: this.getFinancialServicesRoutes(),
        everyAssociatedRightName: [
          'Manager - Allow reporting access',
          'Manager - Allow payments access'
        ]
      }
    ];
    const routes$: Observable<any>[] = this.permission.accessibleMainRoutes(
      mainRoutes
    );

    routes$.push(this.getDashboardRoutes());

    return combineLatest(routes$).pipe(
      filter(value => value.some(Boolean)),
      map((value: Array<RouteDefinition>) => {
        return value.filter(Boolean).sort(this.sortRoutes);
      })
    );
  }

  private getDashboardRoutes(): Observable<RouteDefinition> {
    return this.isAuthenticatedPartnerThatCanSeeReports().pipe(
      map((info: AuthenticatedPartnerThatCanSeeReports) => {
        if (
          info.isAuthenticated &&
          info.hasManagerPermission &&
          (info.isLoggedInAsPartner || info.hasManagerAccess) &&
          info.canSeeReports
        ) {
          return DashboardRouteDefinition.getRoutes();
        } else {
          return null;
        }
      })
    );
  }

  private getReportsRoutes(): Observable<RouteDefinition> {
    return combineLatest(
      this.isAuthenticatedPartnerThatCanSeeReports(),
      this.showClosingReports$,
      this.showIrsReports$,
      this.isBlackboxV2Account$
    ).pipe(
      map(
        (
          value: [
            AuthenticatedPartnerThatCanSeeReports,
            boolean,
            boolean,
            boolean
          ]
        ) => ({
          parent: value[0],
          showClosingReports: value[1],
          showIrsReports: value[2],
          isBlackboxV2Account: value[3]
        })
      ),
      map((info: AuthenticatedPartnerWitTaxReports) => {
        if (
          info.parent.isAuthenticated &&
          info.parent.isValidAppVersion &&
          info.parent.hasManagerPermission &&
          (info.parent.isLoggedInAsPartner || info.parent.hasManagerAccess) &&
          info.parent.canSeeReports
        ) {
          const menuItems: RouteDefinition = ReportsRouteDefinition.getRoutes(
            info.showClosingReports,
            info.showIrsReports,
            info.isBlackboxV2Account
          );
          menuItems.children.sort(this.sortRoutes);
          return menuItems;
        } else {
          return null;
        }
      })
    );
  }

  private getCombinedReportsRoutes(): Observable<RouteDefinition> {
    return combineLatest(
      this.isAuthenticatedPartnerThatCanSeeReports(),
      this.hasMultipleLocations$,
      this.numberOfLocations$
    ).pipe(
      map(
        (value: [AuthenticatedPartnerThatCanSeeReports, boolean, number]) => ({
          parent: value[0],
          hasMultipleLocations: value[1],
          numberOfLocations: value[2]
        })
      ),
      map((info: AuthenticatedPartnerWithReportsAndEstablishments) => {
        if (
          info.parent.isAuthenticated &&
          info.parent.isValidAppVersion &&
          info.parent.hasManagerPermission &&
          (info.parent.isLoggedInAsPartner || info.parent.hasManagerAccess) &&
          info.parent.canSeeReports &&
          info.hasMultipleLocations
        ) {
          const menuItems: RouteDefinition = CombinedReportsRouteDefinition.getRoutes();
          menuItems.children.sort(this.sortRoutes);
          if (info.numberOfLocations > 20) {
            menuItems.children.splice(1, menuItems.children.length - 3);
          }
          return menuItems;
        } else {
          return null;
        }
      })
    );
  }

  private getUserManagementRoutes(): Observable<RouteDefinition> {
    return combineLatest(
      this.isAuthenticatedPartnerWithValidAppVersion(),
      this.companySettingsServiceHelper.isCompanyIn('us'),
      this.companySettingsServiceHelper.isCompanyIn('canada'),
      this.companySettingsServiceHelper.isCompanyIn('singapore'),
      this.companySettingsServiceHelper.isCompanyIn('france'),
      this.companySettingsServiceHelper.isCompanyIn('monaco')
    ).pipe(
      map(
        ([
          info,
          isCompanyInUS,
          isCompanyInCanada,
          isCompanyInSingapore,
          isCompanyInFrance,
          isCompanyInMonaco
        ]: [
          AuthenticatedPartnerWithValidAppVersion,
          boolean,
          boolean,
          boolean,
          boolean,
          boolean
        ]) => {
          if (
            info.isAuthenticated &&
            info.isValidAppVersion &&
            info.hasManagerPermission &&
            (info.isLoggedInAsPartner || info.hasManagerAccess)
          ) {
            const menuItems: RouteDefinition = UserManagementRouteDefinition.getRoutes(
              isCompanyInUS ||
                isCompanyInCanada ||
                isCompanyInSingapore ||
                isCompanyInFrance ||
                isCompanyInMonaco
            );
            menuItems.children.sort(this.sortRoutes);
            return menuItems;
          } else {
            return null;
          }
        }
      )
    );
  }

  private getProductsRoutes(): Observable<RouteDefinition> {
    return combineLatest(
      this.isAuthenticatedPartnerWithValidAppVersion(),
      this.hasProductsAccess$
    ).pipe(
      map(
        ([parent, hasProductAccess]: [
          AuthenticatedPartnerWithValidAppVersion,
          boolean
        ]) => {
          if (
            parent.isAuthenticated &&
            parent.isValidAppVersion &&
            parent.hasManagerPermission &&
            (parent.isLoggedInAsPartner || parent.hasManagerAccess) &&
            hasProductAccess
          ) {
            const menuItems: RouteDefinition = ProductsRouteDefinition.getRoutes();
            menuItems.children.sort(this.sortRoutes);
            return menuItems;
          } else {
            return null;
          }
        }
      )
    );
  }

  private getFloorsRoutes(): Observable<RouteDefinition> {
    return this.isAuthenticatedPartnerWithValidAppVersion().pipe(
      map((info: AuthenticatedPartnerWithValidAppVersion) => {
        if (
          info.isAuthenticated &&
          info.isValidAppVersion &&
          info.hasManagerPermission &&
          (info.isLoggedInAsPartner || info.hasManagerAccess)
        ) {
          return FloorRouteDefinition.getRoutes();
        } else {
          return null;
        }
      })
    );
  }

  private getDeviceManagementRoutes(): Observable<RouteDefinition> {
    return combineLatest(
      this.isAuthenticatedPartnerWithValidAppVersion(),
      this.hasKitchenModuleEnabled$,
      this.isAllowedToConfigureCFD$
    ).pipe(
      map(
        (
          value: [AuthenticatedPartnerWithValidAppVersion, boolean, boolean]
        ) => ({
          parent: value[0],
          hasKitchenModuleEnabled: value[1],
          isAllowedToConfigureCFD: value[2]
        })
      ),
      map((info: AuthenticatedPartnerWithDevices) => {
        if (
          info.parent.isAuthenticated &&
          info.parent.isValidAppVersion &&
          info.parent.hasManagerPermission &&
          (info.parent.isLoggedInAsPartner || info.parent.hasManagerAccess)
        ) {
          const menuItems: RouteDefinition = DeviceManagementRouteDefinition.getRoutes(
            info.hasKitchenModuleEnabled,
            info.isAllowedToConfigureCFD
          );
          menuItems.children.sort(this.sortRoutes);
          return menuItems;
        } else {
          return null;
        }
      })
    );
  }

  private getPrintingRoutes(): Observable<RouteDefinition> {
    return this.isAuthenticatedPartnerWithValidAppVersion().pipe(
      map((info: AuthenticatedPartnerWithValidAppVersion) => {
        if (
          info.isAuthenticated &&
          info.isValidAppVersion &&
          info.hasManagerPermission &&
          (info.isLoggedInAsPartner || info.hasManagerAccess)
        ) {
          const menuItems: RouteDefinition = PrintingRouteDefinition.getRoutes();
          menuItems.children.sort(this.sortRoutes);
          return menuItems;
        } else {
          return null;
        }
      })
    );
  }

  private getSupportRoutes(): Observable<RouteDefinition> {
    return this.isAuthenticatedPartnerWithValidAppVersion().pipe(
      map((info: AuthenticatedPartnerWithValidAppVersion) => {
        if (
          info.isAuthenticated &&
          info.isValidAppVersion &&
          info.hasManagerPermission &&
          (info.isLoggedInAsPartner || info.hasManagerAccess)
        ) {
          const menuItems: RouteDefinition = SupportRouteDefinition.getRoutes();
          menuItems.children.sort(this.sortRoutes);
          return menuItems;
        } else {
          return null;
        }
      })
    );
  }

  private getPaymentsRoutes(): Observable<RouteDefinition> {
    return combineLatest(
      this.isAuthenticatedPartnerThatCanSeeReports(),
      this.paymentsServices.isProcessingEnabled(),
      this.paymentsServices.isProcessorsEnabled(),
      this.paymentsServices.isTerminalsEnabled()
    ).pipe(
      map(
        (
          info: [
            AuthenticatedPartnerThatCanSeeReports,
            boolean,
            boolean,
            boolean
          ]
        ) => ({
          parent: info[0],
          hasPaymentProcessingEnabled: info[1],
          hasPaymentProcessorEnabled: info[2],
          hasPaymentTerminalEnabled: info[3]
        })
      ),
      map((info: AuthenticatedPartnerWithPayments) => {
        if (
          info.parent.isAuthenticated &&
          info.parent.isValidAppVersion &&
          info.parent.hasManagerPermission &&
          (info.parent.isLoggedInAsPartner || info.parent.hasManagerAccess)
        ) {
          const menuItems: RouteDefinition = PaymentsRouteDefinition.getRoutes(
            info.hasPaymentProcessorEnabled,
            info.hasPaymentTerminalEnabled,
            info.hasPaymentProcessingEnabled,
            info.parent.canSeeReports
          );
          menuItems.children.sort(this.sortRoutes);
          return menuItems;
        } else {
          return null;
        }
      })
    );
  }

  private getSettingsRoutes(): Observable<RouteDefinition> {
    return combineLatest(
      this.isAuthenticatedPartnerWithValidAppVersion(),
      this.companySettingsServiceHelper.isCompanyIn('us'),
      this.companySettingsServiceHelper.isCompanyIn('canada'),
      this.companySettingsServiceHelper.isCompanyIn('ireland'),
      this.companySettingsServiceHelper.isCompanyIn('malta'),
      this.companySettingsServiceHelper.isCompanyIn('spain'),
      this.companySettingsServiceHelper.isCompanyIn('uk'),
      this.companySettingsServiceHelper.isCompanyIn('france'),
      this.companySettingsServiceHelper.isCompanyIn('monaco'),
      this.companySettingsServiceHelper.isCompanyIn('guadeloupe'),
      this.companySettingsServiceHelper.isCompanyIn('martinique'),
      this.companySettingsServiceHelper.isCompanyIn('reunion'),
      this.companySettingsServiceHelper.isCompanyIn('belgium'),
      this.companySettingsServiceHelper.isCompanyIn('netherlands'),
      this.isBlackboxAccount$
    ).pipe(
      map(
        (value: Array<AuthenticatedPartnerWithValidAppVersion | boolean>) => ({
          parent: value[0],
          isCompanyInTheUS: value[1],
          isCompanyInCanada: value[2],
          isCompanyInIreland: value[3],
          isCompanyInMalta: value[4],
          isCompanyInSpain: value[5],
          isCompanyInUK: value[6],
          isCompanyInFrance: value[7],
          isCompanyInMonaco: value[8],
          isCompanyInGuadeloupe: value[9],
          isCompanyInMartinique: value[10],
          isCompanyInReunion: value[11],
          isCompanyInBelgium: value[12],
          isCompanyInNetherlands: value[13],
          isBlackboxAccount: value[14]
        })
      ),
      map((info: AuthenticatedSettings) => {
        if (
          info.parent.isAuthenticated &&
          info.parent.isValidAppVersion &&
          info.parent.hasManagerPermission &&
          (info.parent.isLoggedInAsPartner || info.parent.hasManagerAccess)
        ) {
          const showLoyalty: boolean =
            info.isCompanyInTheUS ||
            info.isCompanyInCanada ||
            info.isCompanyInIreland ||
            info.isCompanyInMalta ||
            info.isCompanyInSpain ||
            info.isCompanyInUK ||
            info.isCompanyInBelgium ||
            info.isCompanyInNetherlands ||
            info.isBlackboxAccount;
          const showFDM: boolean =
            info.isCompanyInFrance ||
            info.isCompanyInBelgium ||
            info.isCompanyInMonaco ||
            info.isCompanyInGuadeloupe ||
            info.isCompanyInMartinique ||
            info.isCompanyInReunion ||
            info.isCompanyInCanada ||
            info.isCompanyInNetherlands;
          const menuItems: RouteDefinition = SettingsRouteDefinition.getRoutes(
            showLoyalty,
            showFDM
          );
          menuItems.children.sort(this.sortRoutes);
          return menuItems;
        } else {
          return null;
        }
      })
    );
  }

  private getWaiterRoutes(): Observable<RouteDefinition> {
    return combineLatest(
      this.isAuthenticated$,
      this.hasEmployeePermission$
    ).pipe(
      map((value: [boolean, boolean]) => ({
        isAuthenticated: value[0],
        hasEmployeePermission: value[1]
      })),
      map(
        ({
          isAuthenticated,
          hasEmployeePermission
        }: {
          isAuthenticated: boolean;
          hasEmployeePermission: boolean;
        }) => {
          if (isAuthenticated && hasEmployeePermission) {
            return WaiterRouteDefinition.getRoutes();
          } else {
            return null;
          }
        }
      )
    );
  }

  private getFinancialServicesRoutes(): Observable<RouteDefinition> {
    return combineLatest(
      this.hasManagerAccess$,
      this.isAuthenticated$,
      this.companySettingsServiceHelper.isCompanyIn('us'),
      this.companySettingsServiceHelper.isCompanyIn('uk'),
      this.companySettingsServiceHelper.isCompanyIn('canada'),
      this.companySettingsServiceHelper.isCompanyIn('netherlands'),
      this.companySettingsServiceHelper.isCompanyIn('belgium'),
      this.companySettingsServiceHelper.isCompanyIn('france'),
      this.companySettingsServiceHelper.isCompanyIn('guam')
    ).pipe(
      map(
        ([
          hasManagerAccess$,
          isAuthenticated$,
          isCompanyInTheUS,
          isCompanyInUK,
          isCompanyInCanada,
          isCompanyInNetherlands,
          isCompanyInBelgium,
          isCompanyInFrance,
          isCompanyInGuam
        ]: [
          boolean,
          boolean,
          boolean,
          boolean,
          boolean,
          boolean,
          boolean,
          boolean,
          boolean
        ]) => {
          if (
            hasManagerAccess$ &&
            isAuthenticated$ &&
            (isCompanyInTheUS ||
              isCompanyInUK ||
              isCompanyInCanada ||
              isCompanyInNetherlands ||
              isCompanyInBelgium ||
              isCompanyInFrance ||
              isCompanyInGuam)
          ) {
            return FinancialServicesRouteDefinition.getRoutes();
          } else {
            return null;
          }
        }
      )
    );
  }

  private sortRoutes(routeA: RouteDefinition, routeB: RouteDefinition): number {
    const positionA: number = (routeA && routeA.position) || 0;
    const positionB: number = (routeB && routeB.position) || 0;
    return positionA - positionB;
  }

  private isAuthenticatedPartner(): Observable<AuthenticatedPartner> {
    return combineLatest(
      this.hasManagerAccess$,
      this.accessControl.hasManagerPermission(),
      this.isAuthenticated$,
      this.isLoggedInAsPartner$
    ).pipe(
      map((value: Array<boolean>) => ({
        hasManagerAccess: value[0],
        hasManagerPermission: value[1],
        isAuthenticated: value[2],
        isLoggedInAsPartner: value[3]
      }))
    );
  }

  private getTasksRoutes(): Observable<RouteDefinition> {
    return this.isAuthenticatedPartnerWithValidAppVersion().pipe(
      map((info: AuthenticatedPartnerWithValidAppVersion) => {
        if (
          info.isAuthenticated &&
          info.isValidAppVersion &&
          info.hasManagerPermission &&
          (info.isLoggedInAsPartner || info.hasManagerAccess) &&
          environment.tasks
        ) {
          return TasksRouteDefinition.getRoutes();
        } else {
          return null;
        }
      })
    );
  }

  private getLsAccountingRoutes(): Observable<RouteDefinition> {
    return combineLatest(
      this.isAuthenticatedPartnerWithValidAppVersion(),
      this.isLsAccountingEnabled$
    ).pipe(
      map(([info, isLsAccountingEnabled]) => {
        if (
          isLsAccountingEnabled &&
          info.isAuthenticated &&
          info.isValidAppVersion &&
          info.hasManagerPermission &&
          (info.isLoggedInAsPartner || info.hasManagerAccess)
        ) {
          return LsAccountingRouteDefinition.getRoutes();
        } else {
          return null;
        }
      })
    );
  }

  private isAuthenticatedPartnerWithValidAppVersion(): Observable<
    AuthenticatedPartnerWithValidAppVersion
  > {
    return combineLatest(
      this.isAuthenticatedPartner(),
      this.isValidAppVersion$
    ).pipe(
      map((value: [AuthenticatedPartner, boolean]) => {
        return {
          hasManagerAccess: value[0].hasManagerAccess,
          hasManagerPermission: value[0].hasManagerPermission,
          isAuthenticated: value[0].isAuthenticated,
          isLoggedInAsPartner: value[0].isLoggedInAsPartner,
          isValidAppVersion: value[1]
        };
      })
    );
  }

  private isAuthenticatedPartnerThatCanSeeReports(): Observable<
    AuthenticatedPartnerThatCanSeeReports
  > {
    return combineLatest(
      this.isAuthenticatedPartnerWithValidAppVersion(),
      this.canSeeReports$
    ).pipe(
      map((value: [AuthenticatedPartnerWithValidAppVersion, boolean]) => ({
        hasManagerAccess: value[0].hasManagerAccess,
        hasManagerPermission: value[0].hasManagerPermission,
        isAuthenticated: value[0].isAuthenticated,
        isLoggedInAsPartner: value[0].isLoggedInAsPartner,
        isValidAppVersion: value[0].isValidAppVersion,
        canSeeReports: value[1]
      }))
    );
  }
}

interface AuthenticatedPartner {
  hasManagerAccess: boolean;
  hasManagerPermission: boolean;
  isAuthenticated: boolean;
  isLoggedInAsPartner: boolean;
}

interface AuthenticatedPartnerWithValidAppVersion extends AuthenticatedPartner {
  parent?: AuthenticatedPartner;
  isValidAppVersion: boolean;
}

interface AuthenticatedPartnerThatCanSeeReports
  extends AuthenticatedPartnerWithValidAppVersion {
  parent?: AuthenticatedPartnerWithValidAppVersion;
  canSeeReports: boolean;
}

interface AuthenticatedPartnerWithReportsAndEstablishments
  extends AuthenticatedPartnerThatCanSeeReports {
  parent: AuthenticatedPartnerThatCanSeeReports;
  hasMultipleLocations: boolean;
  numberOfLocations: number;
}

interface AuthenticatedPartnerWitTaxReports
  extends AuthenticatedPartnerThatCanSeeReports {
  parent: AuthenticatedPartnerThatCanSeeReports;
  showClosingReports: boolean;
  showIrsReports: boolean;
  hasLSPaymentEnabled: boolean;
  isBlackboxV2Account: boolean;
}

interface AuthenticatedPartnerWithDevices
  extends AuthenticatedPartnerWithValidAppVersion {
  parent: AuthenticatedPartnerWithValidAppVersion;
  hasKitchenModuleEnabled: boolean;
  isAllowedToConfigureCFD: boolean;
}

interface AuthenticatedPartnerWithPayments
  extends AuthenticatedPartnerThatCanSeeReports {
  parent: AuthenticatedPartnerThatCanSeeReports;
  hasPaymentProcessingEnabled: boolean;
  hasPaymentProcessorEnabled: boolean;
  hasPaymentTerminalEnabled: boolean;
}

interface AuthenticatedSettings
  extends AuthenticatedPartnerWithValidAppVersion {
  parent: AuthenticatedPartnerWithValidAppVersion;
  isCompanyInTheUS: boolean;
  isCompanyInCanada: boolean;
  isCompanyInIreland: boolean;
  isCompanyInMalta: boolean;
  isCompanyInSpain: boolean;
  isCompanyInUK: boolean;
  isCompanyInFrance: boolean;
  isCompanyInBelgium: boolean;
  isCompanyInMonaco: boolean;
  isCompanyInGuadeloupe: boolean;
  isCompanyInMartinique: boolean;
  isCompanyInReunion: boolean;
  isCompanyInNetherlands: boolean;
  isBlackboxAccount: boolean;
}
